// Generative adaptive music — verbatim port of v1 js/music-manager.js
// (chord progressions, drum/stab patterns, scales, BPM curves, combat duck).
// Uses the npm `tone` package (same major as v1's vendored 14.x).

import * as Tone from 'tone';

const VOL = 0.38;
const T = true;
const _ = null;

type Env = 'suburban' | 'residential' | 'urban' | 'industrial' | 'urban-dense';

interface Chord {
  root: string;
  pad: string[];
  arp: string[];
}

const CHORD_PROGRESSIONS: Record<Env, Chord[]> = {
  suburban: [
    { root: 'C2', pad: ['C3', 'E3', 'G3'], arp: ['C5', 'E5', 'G5', 'E5', 'C5', 'G4', 'E5', 'G5'] },
    { root: 'A1', pad: ['A2', 'C3', 'E3'], arp: ['A4', 'C5', 'E5', 'C5', 'A4', 'E5', 'C5', 'A4'] },
    { root: 'F1', pad: ['F2', 'A2', 'C3'], arp: ['F4', 'A4', 'C5', 'A4', 'F4', 'C5', 'A4', 'F4'] },
    { root: 'G1', pad: ['G2', 'B2', 'D3'], arp: ['G4', 'B4', 'D5', 'B4', 'G4', 'D5', 'B4', 'G4'] },
  ],
  residential: [
    {
      root: 'D2',
      pad: ['D3', 'F3', 'A3', 'C4'],
      arp: ['D5', 'F5', 'A5', 'C6', 'A5', 'F5', 'D5', 'F5'],
    },
    {
      root: 'Bb1',
      pad: ['Bb2', 'D3', 'F3'],
      arp: ['Bb4', 'D5', 'F5', 'D5', 'Bb4', 'F5', 'D5', 'Bb4'],
    },
    { root: 'C2', pad: ['C3', 'E3', 'G3'], arp: ['C5', 'E5', 'G5', 'E5', 'C5', 'G5', 'E5', 'C5'] },
    {
      root: 'A1',
      pad: ['A2', 'C3', 'E3', 'G3'],
      arp: ['A4', 'C5', 'E5', 'G5', 'E5', 'C5', 'A4', 'C5'],
    },
  ],
  urban: [
    { root: 'G2', pad: ['G3', 'B3', 'D4'], arp: ['G4', 'B4', 'D5', 'G5', 'D5', 'B4', 'G4', 'B4'] },
    { root: 'C2', pad: ['C3', 'E3', 'G3'], arp: ['C5', 'E5', 'G5', 'C6', 'G5', 'E5', 'C5', 'E5'] },
    { root: 'F2', pad: ['F3', 'A3', 'C4'], arp: ['F4', 'A4', 'C5', 'F5', 'C5', 'A4', 'F4', 'A4'] },
    {
      root: 'G2',
      pad: ['G3', 'B3', 'D4', 'F4'],
      arp: ['G4', 'D5', 'F5', 'B4', 'D5', 'G4', 'B4', 'D5'],
    },
  ],
  industrial: [
    { root: 'E2', pad: ['E3', 'G3', 'B3'], arp: ['E5', 'G5', 'B5', 'G5', 'E5', 'B4', 'G4', 'E4'] },
    { root: 'C2', pad: ['C3', 'E3', 'G3'], arp: ['C5', 'G5', 'E5', 'C5', 'E5', 'G5', 'C5', 'E5'] },
    { root: 'D2', pad: ['D3', 'F3', 'A3'], arp: ['D5', 'F5', 'A5', 'F5', 'D5', 'A4', 'F4', 'D4'] },
    {
      root: 'B1',
      pad: ['B2', 'D#3', 'F#3'],
      arp: ['B4', 'D#5', 'F#5', 'B5', 'F#5', 'D#5', 'B4', 'D#5'],
    },
  ],
  'urban-dense': [
    { root: 'A1', pad: ['A3', 'C4', 'E4'], arp: ['A4', 'C5', 'E5', 'A5', 'E5', 'C5', 'A4', 'C5'] },
    { root: 'G1', pad: ['G3', 'B3', 'D4'], arp: ['G4', 'B4', 'D5', 'G5', 'D5', 'B4', 'G4', 'B4'] },
    { root: 'F1', pad: ['F3', 'A3', 'C4'], arp: ['F4', 'A4', 'C5', 'F5', 'C5', 'A4', 'F4', 'A4'] },
    {
      root: 'E1',
      pad: ['E3', 'G#3', 'B3'],
      arp: ['E4', 'G#4', 'B4', 'E5', 'B4', 'G#4', 'E4', 'G#4'],
    },
  ],
};

const SCALES: Record<Env, string[]> = {
  suburban: ['C4', 'D4', 'E4', 'G4', 'A4', 'C5', 'D5', 'E5', 'G5'],
  residential: ['D4', 'F4', 'G4', 'A4', 'Bb4', 'C5', 'D5', 'F5'],
  urban: ['G4', 'A4', 'B4', 'C5', 'D5', 'E5', 'F5', 'G5'],
  industrial: ['E4', 'F4', 'G4', 'A4', 'B4', 'C5', 'D5', 'E5'],
  'urban-dense': ['A4', 'C5', 'D5', 'E5', 'G5', 'A5'],
};

const BASE_BPMS: Record<Env, number> = {
  suburban: 78,
  residential: 88,
  urban: 96,
  industrial: 112,
  'urban-dense': 128,
};

type Pattern = (boolean | null)[];

const DRUM_PATTERNS: Record<Env, { kick: Pattern; snare: Pattern; hihat: Pattern }> = {
  suburban: {
    // prettier-ignore
    kick: [T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,T,_,_],
    // prettier-ignore
    snare: [_,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_],
    // prettier-ignore
    hihat: [_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,T],
  },
  residential: {
    // prettier-ignore
    kick: [T,_,_,_,_,_,_,T,_,_,_,_,T,_,_,_,T,_,_,_,_,_,_,T,_,_,_,_,T,_,_,_],
    // prettier-ignore
    snare: [_,_,_,_,T,_,T,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,T,_,_,_,_,_,T,_,T,_],
    // prettier-ignore
    hihat: [T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,T],
  },
  urban: {
    // prettier-ignore
    kick: [T,_,_,_,_,_,T,_,_,_,_,_,T,_,_,_,T,_,_,_,_,_,T,_,_,_,T,_,_,_,_,_],
    // prettier-ignore
    snare: [_,_,_,_,T,_,_,_,_,T,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,T,T,_,_,_],
    // prettier-ignore
    hihat: [T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T],
  },
  industrial: {
    // prettier-ignore
    kick: [T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,T,_],
    // prettier-ignore
    snare: [_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,T,_,_],
    // prettier-ignore
    hihat: [T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T,T],
  },
  'urban-dense': {
    // prettier-ignore
    kick: [T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,_,_,T,_,T,_],
    // prettier-ignore
    snare: [_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_],
    // prettier-ignore
    hihat: [T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,_,T,T,T,_,T,_,T,_,T,_,T,T,T,_],
  },
};

const STAB_PATTERNS: Record<Env, Pattern | null> = {
  suburban: null,
  // prettier-ignore
  residential: [_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,T],
  // prettier-ignore
  urban: [_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,T,_,_,_,_,T,_],
  // prettier-ignore
  industrial: [_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,T,_],
  // prettier-ignore
  'urban-dense': [T,_,_,_,_,_,T,_,_,_,_,_,_,_,T,_,T,_,_,_,_,_,T,_,_,_,_,T,_,_,T,_],
};

const STAB_CHORDS: Partial<Record<Env, string[]>> = {
  residential: ['D4', 'F4', 'A4'],
  urban: ['G4', 'B4', 'D5'],
  industrial: ['E4', 'B4'],
  'urban-dense': ['A4', 'C5', 'E5'],
};

const ARP_VOL: Record<Env, number> = {
  suburban: 0.15,
  residential: 0.2,
  urban: 0.25,
  industrial: 0.2,
  'urban-dense': 0.28,
};

const STAB_VOL: Record<Env, number> = {
  suburban: 0,
  residential: 0.28,
  urban: 0.4,
  industrial: 0.5,
  'urban-dense': 0.45,
};

export class MusicSystem {
  isMuted = false;
  private initialized = false;
  private environment: Env = 'urban';
  private baseBpm = 96;
  private isInCombat = false;
  private currentGameSpeed = 0.1;
  private lastSpeedUpdate = 0;
  private currentChordIdx = 0;
  private chordProgression: Chord[] = [];

  private masterGain: Tone.Gain | null = null;
  private compressor: Tone.Compressor | null = null;
  private reverb: Tone.Reverb | null = null;
  private gains: Record<string, Tone.Gain> = {};
  private synths: { dispose(): void }[] = [];
  private kick!: Tone.MembraneSynth;
  private snare!: Tone.NoiseSynth;
  private hihat!: Tone.MetalSynth;
  private bass!: Tone.MonoSynth;
  private pad!: Tone.PolySynth;
  private melody!: Tone.Synth;
  private arp!: Tone.Synth;
  private stab!: Tone.PolySynth;
  private intensitySynth!: Tone.Synth;
  private sequences: Tone.Sequence[] = [];
  private scheduleIds: number[] = [];

  async init(env: string): Promise<void> {
    if (this.initialized) this.stop();
    await Tone.start();

    this.environment = (env in BASE_BPMS ? env : 'urban') as Env;
    this.baseBpm = BASE_BPMS[this.environment];
    this.chordProgression = CHORD_PROGRESSIONS[this.environment];
    this.currentChordIdx = 0;

    Tone.getTransport().bpm.value = this.baseBpm;

    this.buildChain();
    this.buildSynths();
    this.startChordProgression();
    this.startDrums();
    this.startArpeggio();
    this.startGenerativeMelody();
    this.startStabs();
    this.startIntensityLayer();

    this.masterGain?.gain.setValueAtTime(0, Tone.now());
    if (!this.isMuted) this.masterGain?.gain.rampTo(VOL, 2.5);

    if (Tone.getTransport().state !== 'started') Tone.getTransport().start();
    this.initialized = true;
  }

  private buildChain(): void {
    this.masterGain = new Tone.Gain(0).toDestination();
    this.compressor = new Tone.Compressor({
      threshold: -18,
      ratio: 4,
      attack: 0.003,
      release: 0.1,
    }).connect(this.masterGain);
    this.reverb = new Tone.Reverb({ decay: 2.5, preDelay: 0.01, wet: 0.3 }).connect(
      this.masterGain,
    );

    this.gains = {
      kick: new Tone.Gain(1.0).connect(this.compressor),
      snare: new Tone.Gain(0.6).connect(this.compressor),
      hihat: new Tone.Gain(0.12).connect(this.compressor),
      bass: new Tone.Gain(0.8).connect(this.compressor),
      pad: new Tone.Gain(0.4).connect(this.reverb),
      melody: new Tone.Gain(0.55).connect(this.compressor),
      arp: new Tone.Gain(0.2).connect(this.reverb),
      stab: new Tone.Gain(0).connect(this.compressor),
      intensity: new Tone.Gain(0).connect(this.compressor),
    };
  }

  private buildSynths(): void {
    const env = this.environment;
    this.kick = new Tone.MembraneSynth({
      pitchDecay: 0.08,
      octaves: 5,
      envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.1 },
    }).connect(this.gains['kick'] as Tone.Gain);

    this.snare = new Tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.15, sustain: 0, release: 0.05 },
    }).connect(this.gains['snare'] as Tone.Gain);

    this.hihat = new Tone.MetalSynth({
      harmonicity: 5.1,
      modulationIndex: 32,
      resonance: 4000,
      octaves: 1.5,
      envelope: { attack: 0.001, decay: 0.08, release: 0.01 },
    }).connect(this.gains['hihat'] as Tone.Gain);

    const bassOsc: Record<Env, string> = {
      suburban: 'triangle',
      residential: 'sine',
      urban: 'sawtooth',
      industrial: 'square',
      'urban-dense': 'sawtooth',
    };
    this.bass = new Tone.MonoSynth({
      oscillator: { type: bassOsc[env] as 'sawtooth' },
      filter: { Q: 4, type: 'lowpass', rolloff: -24 },
      envelope: { attack: 0.03, decay: 0.2, sustain: 0.7, release: 0.5 },
      filterEnvelope: {
        attack: 0.05,
        decay: 0.2,
        sustain: 0.4,
        release: 0.4,
        baseFrequency: 250,
        octaves: 2.5,
      },
    }).connect(this.gains['bass'] as Tone.Gain);

    this.pad = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: { attack: 0.5, decay: 0.5, sustain: 0.8, release: 2.0 },
    }).connect(this.gains['pad'] as Tone.Gain);

    const melOsc: Record<Env, string> = {
      suburban: 'sine',
      residential: 'triangle',
      urban: 'triangle',
      industrial: 'sawtooth',
      'urban-dense': 'square',
    };
    this.melody = new Tone.Synth({
      oscillator: { type: melOsc[env] as 'triangle' },
      envelope: { attack: 0.04, decay: 0.15, sustain: 0.5, release: 0.5 },
    }).connect(this.gains['melody'] as Tone.Gain);

    this.arp = new Tone.Synth({
      oscillator: { type: 'sine' },
      envelope: { attack: 0.01, decay: 0.12, sustain: 0.15, release: 0.2 },
    }).connect(this.gains['arp'] as Tone.Gain);

    this.stab = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sawtooth' },
      envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.08 },
    }).connect(this.gains['stab'] as Tone.Gain);

    this.intensitySynth = new Tone.Synth({
      oscillator: { type: 'square' },
      envelope: { attack: 0.01, decay: 0.08, sustain: 0.2, release: 0.15 },
    }).connect(this.gains['intensity'] as Tone.Gain);

    this.synths = [
      this.kick,
      this.snare,
      this.hihat,
      this.bass,
      this.pad,
      this.melody,
      this.arp,
      this.stab,
      this.intensitySynth,
    ];
  }

  private startChordProgression(): void {
    let barCount = 0;
    const id = Tone.getTransport().scheduleRepeat(
      (time) => {
        this.currentChordIdx = barCount % this.chordProgression.length;
        barCount++;
        const chord = this.chordProgression[this.currentChordIdx];
        if (!chord) return;
        this.pad.triggerAttackRelease(chord.pad, '2n', time, 0.38);
        this.bass.triggerAttackRelease(chord.root, '2n', time, 0.8);
      },
      '1m',
      0,
    );
    this.scheduleIds.push(id);
  }

  private startDrums(): void {
    const dp = DRUM_PATTERNS[this.environment];
    const kickSeq = new Tone.Sequence(
      (time) => this.kick.triggerAttackRelease('C1', '8n', time, 0.9),
      dp.kick,
      '16n',
    ).start(0);
    const snareSeq = new Tone.Sequence(
      (time) => this.snare.triggerAttackRelease('8n', time, 0.55),
      dp.snare,
      '16n',
    ).start(0);
    const hihatSeq = new Tone.Sequence(
      (time) => this.hihat.triggerAttackRelease('16n', time, 0.25),
      dp.hihat,
      '16n',
    ).start(0);
    this.sequences.push(
      kickSeq as Tone.Sequence,
      snareSeq as Tone.Sequence,
      hihatSeq as Tone.Sequence,
    );
  }

  private startArpeggio(): void {
    const env = this.environment;
    const interval = {
      suburban: '4n',
      residential: '8n',
      urban: '8n',
      industrial: '8n',
      'urban-dense': '16n',
    }[env];
    (this.gains['arp'] as Tone.Gain).gain.value = ARP_VOL[env];

    let step = 0;
    const id = Tone.getTransport().scheduleRepeat(
      (time) => {
        const arpNotes = this.chordProgression[this.currentChordIdx]?.arp;
        if (!arpNotes) return;
        this.arp.triggerAttackRelease(
          arpNotes[step % arpNotes.length] as string,
          interval,
          time,
          0.15 + Math.random() * 0.08,
        );
        step++;
      },
      interval,
      0,
    );
    this.scheduleIds.push(id);
  }

  private startGenerativeMelody(): void {
    const scale = SCALES[this.environment];
    const beatProb = [0.85, 0.28, 0.65, 0.18, 0.8, 0.22, 0.6, 0.12];
    let prevIdx = Math.floor(scale.length / 2);
    let step = 0;
    let restSteps = 0;
    let phraseLeft = 0;

    const id = Tone.getTransport().scheduleRepeat(
      (time) => {
        if (restSteps > 0) {
          restSteps--;
          return;
        }
        const beat = step % 8;
        step++;
        if (phraseLeft <= 0) {
          if (Math.random() > 0.5) return;
          phraseLeft = 4 + Math.floor(Math.random() * 5);
        }
        if (Math.random() > (beatProb[beat] as number)) return;

        const r = Math.random();
        if (r < 0.44) {
          prevIdx = Math.max(
            0,
            Math.min(scale.length - 1, prevIdx + (Math.random() < 0.55 ? 1 : -1)),
          );
        } else if (r < 0.68) {
          prevIdx = Math.max(
            0,
            Math.min(scale.length - 1, prevIdx + (Math.random() < 0.5 ? 2 : -2)),
          );
        } else if (r < 0.87) {
          // repeat same note
        } else {
          prevIdx = Math.floor(Math.random() * scale.length);
        }

        const dur = ['8n', '8n', '8n', '4n', '4n', '4n.'][Math.floor(Math.random() * 6)] as string;
        this.melody.triggerAttackRelease(
          scale[prevIdx] as string,
          dur,
          time,
          0.45 + Math.random() * 0.28,
        );

        phraseLeft--;
        if (phraseLeft <= 0) restSteps = 2 + Math.floor(Math.random() * 4);
      },
      '8n',
      0,
    );
    this.scheduleIds.push(id);
  }

  private startStabs(): void {
    const pattern = STAB_PATTERNS[this.environment];
    const chord = STAB_CHORDS[this.environment];
    if (!pattern || !chord) return;
    (this.gains['stab'] as Tone.Gain).gain.value = STAB_VOL[this.environment];
    const stabSeq = new Tone.Sequence(
      (time) => this.stab.triggerAttackRelease(chord, '16n', time, 0.65),
      pattern,
      '16n',
    ).start(0);
    this.sequences.push(stabSeq as Tone.Sequence);
  }

  private startIntensityLayer(): void {
    const scale = SCALES[this.environment];
    const highScale = scale.map((n) => n.replace(/(\d+)$/, (oct) => String(parseInt(oct) + 1)));
    let prevIdx = Math.floor(highScale.length / 2);

    const id = Tone.getTransport().scheduleRepeat(
      (time) => {
        if ((this.gains['intensity'] as Tone.Gain).gain.value < 0.04) return;
        prevIdx = Math.max(
          0,
          Math.min(highScale.length - 1, prevIdx + (Math.random() < 0.5 ? 1 : -1)),
        );
        this.intensitySynth.triggerAttackRelease(
          highScale[prevIdx] as string,
          '16n',
          time,
          0.28 + Math.random() * 0.14,
        );
      },
      '8n',
      0,
    );
    this.scheduleIds.push(id);
  }

  private speedRatio(gameSpeed: number): number {
    return Math.min(Math.max((gameSpeed - 0.09) / (0.25 - 0.09), 0), 1);
  }

  update(gameSpeed: number): void {
    if (!this.initialized || this.isMuted || this.isInCombat) return;
    this.currentGameSpeed = gameSpeed;

    const now = Tone.now();
    if (now - this.lastSpeedUpdate < 1.0) return;
    this.lastSpeedUpdate = now;

    const ratio = this.speedRatio(gameSpeed);
    Tone.getTransport().bpm.rampTo(this.baseBpm + ratio * 28, 3);
    (this.gains['intensity'] as Tone.Gain).gain.rampTo(ratio * 0.5, 2);
    (this.gains['hihat'] as Tone.Gain).gain.rampTo(0.1 + ratio * 0.4, 2);
    (this.gains['melody'] as Tone.Gain).gain.rampTo(0.55 - ratio * 0.15, 2);
    const base = STAB_VOL[this.environment];
    if (base > 0) (this.gains['stab'] as Tone.Gain).gain.rampTo(base + ratio * 0.15, 2);
  }

  onCombatStart(): void {
    if (!this.initialized) return;
    this.isInCombat = true;
    Tone.getTransport().bpm.rampTo(Tone.getTransport().bpm.value * 0.18, 0.6);
    (this.gains['pad'] as Tone.Gain).gain.rampTo(1.0, 0.4);
    (this.gains['melody'] as Tone.Gain).gain.rampTo(0.05, 0.4);
    (this.gains['arp'] as Tone.Gain).gain.rampTo(0, 0.4);
    (this.gains['hihat'] as Tone.Gain).gain.rampTo(0.02, 0.4);
    (this.gains['stab'] as Tone.Gain).gain.rampTo(0, 0.4);
    (this.gains['intensity'] as Tone.Gain).gain.rampTo(0, 0.4);
  }

  onCombatEnd(): void {
    if (!this.initialized) return;
    this.isInCombat = false;
    const ratio = this.speedRatio(this.currentGameSpeed);
    Tone.getTransport().bpm.rampTo(this.baseBpm + ratio * 28, 0.5);
    (this.gains['pad'] as Tone.Gain).gain.rampTo(0.4, 0.5);
    (this.gains['melody'] as Tone.Gain).gain.rampTo(0.55 - ratio * 0.15, 0.5);
    (this.gains['arp'] as Tone.Gain).gain.rampTo(ARP_VOL[this.environment], 0.5);
    (this.gains['hihat'] as Tone.Gain).gain.rampTo(0.1 + ratio * 0.4, 0.5);
    (this.gains['intensity'] as Tone.Gain).gain.rampTo(ratio * 0.5, 0.5);
    (this.gains['stab'] as Tone.Gain).gain.rampTo(STAB_VOL[this.environment] + ratio * 0.15, 0.5);
  }

  onGameOver(): void {
    if (!this.initialized) return;
    this.masterGain?.gain.rampTo(0, 2.0);
    setTimeout(() => {
      try {
        Tone.getTransport().pause();
      } catch {
        /* transport already stopped */
      }
    }, 2100);
  }

  pause(): void {
    if (!this.initialized) return;
    Tone.getTransport().pause();
    this.masterGain?.gain.rampTo(0, 0.3);
  }

  resume(): void {
    if (!this.initialized) return;
    if (Tone.getTransport().state !== 'started') Tone.getTransport().start();
    if (!this.isMuted) this.masterGain?.gain.rampTo(VOL, 0.5);
  }

  setMuted(muted: boolean): void {
    this.isMuted = muted;
    this.masterGain?.gain.rampTo(muted ? 0 : VOL, 0.3);
  }

  stop(): void {
    if (!this.initialized) return;
    this.masterGain?.gain.rampTo(0, 0.15);
    for (const id of this.scheduleIds) {
      try {
        Tone.getTransport().clear(id);
      } catch {
        /* already cleared */
      }
    }
    this.scheduleIds = [];
    for (const s of this.sequences) {
      try {
        s.stop();
        s.dispose();
      } catch {
        /* already disposed */
      }
    }
    this.sequences = [];
    for (const s of [...this.synths, ...Object.values(this.gains)]) {
      try {
        s.dispose();
      } catch {
        /* already disposed */
      }
    }
    this.gains = {};
    for (const node of [this.reverb, this.compressor, this.masterGain]) {
      try {
        node?.dispose();
      } catch {
        /* already disposed */
      }
    }
    this.reverb = null;
    this.compressor = null;
    this.masterGain = null;
    try {
      Tone.getTransport().stop();
      Tone.getTransport().cancel();
    } catch {
      /* transport already stopped */
    }
    this.initialized = false;
  }
}
